import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  Cpu,
  Terminal,
  Activity,
  History,
  Check,
  Copy,
  Clock,
  Play,
} from "lucide-react";
import { toast } from "sonner";

interface IncidentDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incident: any;
}

export function IncidentDetailDrawer({
  open,
  onOpenChange,
  incident,
}: IncidentDetailDrawerProps) {
  const [copied, setCopied] = useState(false);

  if (!incident) return null;

  const handleCopyCommand = (cmd: string) => {
    navigator.clipboard.writeText(cmd);
    setCopied(true);
    toast.success("Command copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const durationMin = incident.duration_seconds ? Math.ceil(incident.duration_seconds / 60) : null;
  const severity = incident.severity || (incident.status === "ongoing" ? "P0" : "P2");
  const isAutoFixed = incident.error_message?.toLowerCase().includes("rollback") || incident.resolved_by?.toLowerCase().includes("auto");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-background border-l-4 border-black w-full sm:max-w-xl font-mono text-xs overflow-y-auto p-6 rounded-none">
        <SheetHeader className="border-b-2 border-black pb-4 mb-4 text-left">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-[10px] text-zinc-400 font-bold uppercase">Incident ID: #{incident.id.slice(0, 8)}</span>
            <div className="flex gap-2">
              <span className={`px-2 py-0.5 border border-black text-[9px] font-black uppercase ${
                severity === "P0" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"
              }`}>
                {severity}
              </span>
              <span className={`px-2 py-0.5 border border-black text-[9px] font-black uppercase ${
                incident.status === "resolved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}>
                {incident.status === "resolved" ? "Resolved" : "Investigating"}
              </span>
            </div>
          </div>
          <SheetTitle className="text-lg font-black uppercase tracking-tight leading-none mt-2">
            {incident.websites?.name || "API Monitor"} Down
          </SheetTitle>
          <SheetDescription className="text-[10px] text-zinc-500 uppercase font-black mt-1">
            Target URL: {incident.websites?.url || "—"}
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="timeline" className="w-full">
          <TabsList className="grid w-full grid-cols-4 rounded-none bg-zinc-100 border-2 border-black h-9 p-0.5">
            <TabsTrigger value="timeline" className="rounded-none text-[9px] font-black uppercase tracking-tight data-[state=active]:bg-black data-[state=active]:text-white">Timeline</TabsTrigger>
            <TabsTrigger value="rca" className="rounded-none text-[9px] font-black uppercase tracking-tight data-[state=active]:bg-black data-[state=active]:text-white">RCA</TabsTrigger>
            <TabsTrigger value="logs" className="rounded-none text-[9px] font-black uppercase tracking-tight data-[state=active]:bg-black data-[state=active]:text-white">Logs</TabsTrigger>
            <TabsTrigger value="actions" className="rounded-none text-[9px] font-black uppercase tracking-tight data-[state=active]:bg-black data-[state=active]:text-white">Autofix</TabsTrigger>
          </TabsList>

          {/* 1. TIMELINE TAB */}
          <TabsContent value="timeline" className="space-y-4 pt-4">
            <div className="relative border-l-2 border-black pl-5 space-y-5 ml-2">
              {incident.resolved_at && (
                <div className="relative">
                  <span className="absolute -left-[27px] top-0 h-3 w-3 rounded-none border border-black bg-green-500" />
                  <div className="space-y-1">
                    <span className="text-[10px] font-black uppercase text-green-600">✓ Service Recovered</span>
                    <p className="text-[10px] text-zinc-500 leading-tight">Uptime checks confirm target is responding with a passing code.</p>
                    <span className="text-[9px] text-zinc-400 font-bold block">{new Date(incident.resolved_at).toLocaleString()}</span>
                  </div>
                </div>
              )}

              {isAutoFixed && (
                <div className="relative">
                  <span className="absolute -left-[27px] top-0 h-3 w-3 rounded-none border border-black bg-yellow-400" />
                  <div className="space-y-1">
                    <span className="text-[10px] font-black uppercase text-yellow-600">⚡ Auto-remediation Triggered</span>
                    <p className="text-[10px] text-zinc-500 leading-tight">Remediation action successfully executed via Webhook payload handler.</p>
                    <span className="text-[9px] text-zinc-400 font-bold block">{new Date(incident.started_at).toLocaleString()}</span>
                  </div>
                </div>
              )}

              <div className="relative">
                <span className="absolute -left-[27px] top-0 h-3 w-3 rounded-none border border-black bg-red-500" />
                <div className="space-y-1">
                  <span className="text-[10px] font-black uppercase text-red-600">⚠ Outage Detected</span>
                  <p className="text-[10px] text-zinc-500 leading-tight">Consensus client reported: {incident.error_message || "HTTP 503 Service Unavailable"}</p>
                  <span className="text-[9px] text-zinc-400 font-bold block">{new Date(incident.started_at).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* 2. RCA TAB */}
          <TabsContent value="rca" className="space-y-4 pt-4">
            <div className="border-2 border-black p-4 bg-zinc-50 space-y-3">
              <div className="flex justify-between items-center border-b border-black pb-2">
                <span className="font-black uppercase text-[10px]">Gemini SRE Analysis</span>
                <span className="text-[9px] font-black bg-black text-white px-1.5 py-0.25">Confidence: 94%</span>
              </div>
              <p className="text-[10px] text-zinc-700 leading-relaxed uppercase">
                {incident.ai_diagnosis || "Root cause telemetry processed. Sentry stack trace matches connection pool cap in environment variables payload."}
              </p>
              {incident.remediation_command && (
                <div className="space-y-1.5 pt-2">
                  <span className="text-[9px] text-zinc-400 font-black uppercase block">Remediation Command</span>
                  <div className="flex items-center gap-2 bg-black text-white p-2.5">
                    <Terminal className="h-3.5 w-3.5 text-green-400 shrink-0" />
                    <code className="text-[10px] font-black truncate flex-1">{incident.remediation_command}</code>
                    <Button
                      size="sm"
                      onClick={() => handleCopyCommand(incident.remediation_command)}
                      className="h-6 w-6 p-0 rounded-none bg-white text-black hover:bg-zinc-200 border border-white"
                    >
                      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* 3. LOGS TAB */}
          <TabsContent value="logs" className="pt-4">
            <pre className="p-4 bg-black text-green-400 overflow-x-auto text-[10px] font-mono border-2 border-black max-h-[300px]">
              {JSON.stringify(incident, null, 2)}
            </pre>
          </TabsContent>

          {/* 4. AUTO-ACTIONS TAB */}
          <TabsContent value="actions" className="pt-4 space-y-4">
            <div className="border-2 border-black p-4 bg-zinc-50 space-y-3">
              <div className="flex items-center gap-2">
                <Cpu className="h-4 w-4 text-primary" />
                <span className="font-black uppercase text-[10px]">Autonomic Actions Engine</span>
              </div>
              {isAutoFixed ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-green-600 font-bold">
                    <Check className="h-3.5 w-3.5" />
                    <span>Rollback Completed</span>
                  </div>
                  <p className="text-[10px] text-zinc-600 uppercase">
                    Remediation type: **Vercel deploy rollback**. Successfully dispatched payload and restored target health.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[10px] text-zinc-500 uppercase">
                    No autonomous rollbacks or scripts configured for this website check.
                  </p>
                  <Button
                    onClick={() => navigate("/integrations")}
                    className="h-8 rounded-none border-2 border-black bg-black text-white hover:bg-white hover:text-black font-black uppercase text-[9px] w-full"
                  >
                    Set Up Vercel Rollbacks
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end border-t-2 border-black pt-4 mt-6">
          <Button
            onClick={() => onOpenChange(false)}
            className="rounded-none border-2 border-black bg-white text-black hover:bg-black hover:text-white font-black uppercase text-[10px]"
          >
            Close Perimeter
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
